import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Search, RefreshCw, AlertTriangle, X, Shield, Ban, CheckCircle,
  Eye, Ticket, User, Clock
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700' },
  resolved: { label: 'Resolvido', color: 'bg-green-100 text-green-700' }
};

const RISK_CONFIG = {
  high: { label: 'Alto', color: 'bg-red-100 text-red-700' },
  medium: { label: 'Médio', color: 'bg-yellow-100 text-yellow-700' },
  low: { label: 'Baixo', color: 'bg-blue-100 text-blue-700' }
};

const VIOLATION_TYPES = {
  multiple_cancellations: 'Múltiplos cancelamentos',
  suspicious_activity: 'Atividade suspeita',
  payment_fraud: 'Fraude de pagamento',
  fake_review: 'Avaliação falsa',
  account_sharing: 'Compartilhamento de conta',
  other: 'Outro'
};

export default function AdminFraud() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '', risk: '' });
  
  // Modal de resolução
  const [selectedLog, setSelectedLog] = useState(null);
  const [resolveAction, setResolveAction] = useState('dismiss');
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchLogs();
  }, [user, navigate]);

  // PROMPT BLINDADO: Refetch quando filtros mudam
  useEffect(() => {
    if (user && user.user_type === 'admin') {
      fetchLogs(true);
    }
  }, [filters.status, filters.risk]);

  const fetchLogs = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.risk) params.append('risk_level', filters.risk);
      
      const res = await axios.get(`${API}/admin/dashboard-v2/fraud?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data.logs || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error(error);
      // Fallback para endpoint antigo
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/fraud-logs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLogs(res.data.logs || []);
      } catch (e) {
        setLogs([]);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedLog) return;
    
    setResolving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/dashboard-v2/fraud/${selectedLog.id}/resolve`, {
        action: resolveAction,
        notes: resolveNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Log resolvido!');
      setSelectedLog(null);
      setResolveNotes('');
      fetchLogs(true);
    } catch (error) {
      toast.error('Erro ao resolver log');
    } finally {
      setResolving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const filteredLogs = logs.filter(log => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matches = 
        log.user?.name?.toLowerCase().includes(search) ||
        log.user?.email?.toLowerCase().includes(search) ||
        log.violation_type?.toLowerCase().includes(search);
      if (!matches) return false;
    }
    return true;
  });

  const clearFilters = () => setFilters({ search: '', status: '', risk: '' });
  const hasFilters = filters.search || filters.status || filters.risk;

  if (loading) {
    return (
      <AdminLayout title="Antifraude" subtitle="Carregando...">
        <div className="space-y-6">
          <div className="grid grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Antifraude</h1>
            <p className="text-slate-500">Monitore e resolva atividades suspeitas</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchLogs(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.status === 'pending' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, status: filters.status === 'pending' ? '' : 'pending' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending || 0}</p>
                <p className="text-xs text-slate-500">Pendentes</p>
              </div>
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.status === 'resolved' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, status: filters.status === 'resolved' ? '' : 'resolved' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.resolved || 0}</p>
                <p className="text-xs text-slate-500">Resolvidos</p>
              </div>
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.risk === 'high' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, risk: filters.risk === 'high' ? '' : 'high' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.high_risk || 0}</p>
                <p className="text-xs text-slate-500">Alto Risco</p>
              </div>
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.risk === 'medium' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, risk: filters.risk === 'medium' ? '' : 'medium' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Shield className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.medium_risk || 0}</p>
                <p className="text-xs text-slate-500">Médio Risco</p>
              </div>
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.risk === 'low' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, risk: filters.risk === 'low' ? '' : 'low' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.low_risk || 0}</p>
                <p className="text-xs text-slate-500">Baixo Risco</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por usuário ou tipo de violação..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Limpar
              </Button>
            )}
          </div>
        </Card>

        {/* Logs List */}
        <div className="space-y-3">
          {filteredLogs.length > 0 ? (
            filteredLogs.map((log) => {
              const statusConfig = STATUS_CONFIG[log.status] || STATUS_CONFIG.pending;
              const riskConfig = RISK_CONFIG[log.risk_level] || RISK_CONFIG.medium;
              
              return (
                <Card key={log.id} className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${riskConfig.color}`}>
                      <AlertTriangle className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-semibold">
                          {VIOLATION_TYPES[log.violation_type] || log.violation_type || 'Violação'}
                        </p>
                        <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                        <Badge className={riskConfig.color}>{riskConfig.label}</Badge>
                      </div>
                      
                      {log.user && (
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                          <User className="w-3 h-3" />
                          <span>{log.user.name || 'Usuário'}</span>
                          <span>•</span>
                          <span>{log.user.email}</span>
                          <Badge variant="outline" className="text-xs">{log.user.user_type}</Badge>
                        </div>
                      )}
                      
                      {log.description && (
                        <p className="text-sm text-slate-600 mt-2">{log.description}</p>
                      )}
                      
                      <p className="text-xs text-slate-400 mt-1">{formatDate(log.created_at)}</p>
                    </div>

                    <div className="flex gap-2">
                      {log.status === 'pending' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedLog(log)}
                        >
                          <Eye className="w-4 h-4 mr-1" /> Resolver
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          ) : (
            <Card className="p-12 text-center">
              <Shield className="w-16 h-16 mx-auto mb-4 text-green-200" />
              <h3 className="text-lg font-semibold mb-2 text-green-700">Nenhuma violação detectada</h3>
              <p className="text-slate-500">
                {hasFilters 
                  ? 'Tente ajustar os filtros'
                  : 'O sistema está funcionando normalmente'}
              </p>
            </Card>
          )}
        </div>
      </div>

      {/* Modal de Resolução */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolver Alerta de Fraude</DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="font-medium text-red-800">
                  {VIOLATION_TYPES[selectedLog.violation_type] || selectedLog.violation_type}
                </p>
                {selectedLog.user && (
                  <p className="text-sm text-red-600">{selectedLog.user.name} - {selectedLog.user.email}</p>
                )}
              </div>

              <div>
                <Label>Ação a tomar</Label>
                <Select value={resolveAction} onValueChange={setResolveAction}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dismiss">Dispensar (falso positivo)</SelectItem>
                    <SelectItem value="block_user">Bloquear usuário</SelectItem>
                    <SelectItem value="open_ticket">Abrir ticket de suporte</SelectItem>
                    <SelectItem value="warn">Enviar advertência</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Observações</Label>
                <Textarea
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  placeholder="Descreva a resolução..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedLog(null)}>Cancelar</Button>
            <Button onClick={handleResolve} disabled={resolving}>
              {resolving ? 'Processando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
